import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { usePostNpsResponseMutation } from "@/api/nps/useNpsAPI";
import { useGetPartnerDetailAPI } from "@/api/partners/usePartnersAPI";
import { CloseIcon } from "@/assets/icons";
import { Button } from "@/components/ui/button";
import {
  NPS_COMMENT_MAX_CHARS,
  NPS_DETRACTOR_MAX,
  NPS_PASSIVE_MAX,
  NPS_THANK_YOU_DELAY_MS
} from "@/constants/nps";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import { setShowNpsModal } from "@/store/slice/uiSlice";
import { getSubdomain } from "@/utils/location";

enum ENpsStep {
  SCORE = "score",
  COMMENT = "comment",
  THANK_YOU = "thank_you"
}

const getScoreButtonStyle = (scoreValue: number) => {
  if (scoreValue <= NPS_DETRACTOR_MAX) {
    return "bg-[#F3B6B6] border-[#E57373]/30 text-[#FFFFFF]";
  }
  if (scoreValue > NPS_DETRACTOR_MAX && scoreValue < NPS_PASSIVE_MAX) {
    return "bg-[#FCDBB3] border-[#FCDBB3]/30 text-[#101010]";
  }
  if (scoreValue == NPS_PASSIVE_MAX) {
    return "bg-[#FBF8B2] border-[#FBF8B2]/30 text-[#101010]";
  }

  return "bg-[#A7F3D0] border-[#34D399]/30 text-[#064E3B]";
};

export const NpsModal = () => {
  const dispatch = useDispatch();
  const { npsId, showNpsModal } = useSelector((state: RootState) => state.ui);
  const { data: partnerData } = useGetPartnerDetailAPI(getSubdomain());

  const [step, setStep] = useState<ENpsStep>(ENpsStep.SCORE);
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState("");

  const { mutate: submitNps, isPending } = usePostNpsResponseMutation({
    onSuccess: () => {
      setStep(ENpsStep.THANK_YOU);
      setTimeout(() => {
        dispatch(setShowNpsModal(false));
      }, NPS_THANK_YOU_DELAY_MS);
    },
    onError: (error) => {
      console.error("Failed to submit NPS response:", error);
      setTimeout(() => {
        dispatch(setShowNpsModal(false));
      }, NPS_THANK_YOU_DELAY_MS);
    }
  });

  const productName = partnerData?.app_title || "LOKASI Intelligence";

  const handleScoreSelect = (selectedScore: number) => {
    setScore(selectedScore);
    setStep(ENpsStep.COMMENT);
  };

  const handleSubmit = () => {
    if (score === null || !npsId) return;

    submitNps({
      npsId,
      payload: {
        score,
        comment: comment
      }
    });
  };

  const getFollowUpQuestion = () => {
    if (score === null) return "";
    if (score <= NPS_DETRACTOR_MAX)
      return "Thanks for your feedback. What is the main reason for your score?";
    if (score <= NPS_PASSIVE_MAX) return "Thanks! What could we do better?";
    return "That's great to hear! What do you love most?";
  };

  const renderScoreStep = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-bold leading-tight text-neutral-900">
        How likely are you to recommend {productName} to a colleague?
      </h3>
      <div className="flex justify-between gap-2">
        {Array.from({ length: 11 }, (_, i) => i).map((scoreValue) => (
          <button
            key={scoreValue}
            onClick={() => handleScoreSelect(scoreValue)}
            className={cn(
              "flex h-10 w-10 items-center justify-center rounded-[6px] border text-sm font-bold transition-all hover:scale-110 cursor-pointer",
              getScoreButtonStyle(scoreValue)
            )}
          >
            {scoreValue}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-neutral-500">
        <span>Very unlikely</span>
        <span>Very likely</span>
      </div>
    </div>
  );

  const renderCommentStep = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold leading-tight text-neutral-900">
        {getFollowUpQuestion()}
      </h3>
      <div className="relative">
        <textarea
          value={comment}
          onChange={(e) =>
            setComment(e.target.value.slice(0, NPS_COMMENT_MAX_CHARS))
          }
          placeholder="Type your answer here..."
          className="min-h-[120px] w-full rounded-md border border-neutral-200 p-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
        />
        <span className="absolute bottom-2 right-2 text-[10px] text-neutral-400">
          {comment.length}/{NPS_COMMENT_MAX_CHARS}
        </span>
      </div>
      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => setStep(ENpsStep.SCORE)}
          disabled={isPending}
        >
          Back
        </Button>
        <Button className="flex-1" onClick={handleSubmit} disabled={isPending}>
          {isPending ? "Submitting..." : "Continue"}
        </Button>
      </div>
    </div>
  );

  const renderThankYouStep = () => (
    <div className="space-y-2 py-4 text-left">
      <h3 className="text-lg font-bold text-neutral-900">Thank you!</h3>
      <p className="text-sm text-neutral-600">
        Thank you for filling out the survey. Your answer will help us better
        serve you as a customer in the future.
      </p>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case ENpsStep.SCORE:
        return renderScoreStep();
      case ENpsStep.COMMENT:
        return renderCommentStep();
      case ENpsStep.THANK_YOU:
        return renderThankYouStep();
      default:
        return renderScoreStep();
    }
  };

  if (!showNpsModal) return null;

  return (
    <div className="fixed bottom-6 left-6 z-[1000] ml-14 w-[568px] animate-in fade-in slide-in-from-bottom-4 duration-300 pointer-events-auto">
      <div className="relative overflow-hidden rounded-2xl bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-neutral-100">
        {step !== ENpsStep.THANK_YOU && (
          <button
            onClick={() => dispatch(setShowNpsModal(false))}
            className="absolute right-4 top-4 text-neutral-400 hover:text-neutral-600 cursor-pointer"
            aria-label="Close"
          >
            <CloseIcon className="w-5 h-5 fill-txtBody" />
          </button>
        )}
        {renderStep()}
      </div>
    </div>
  );
};
